import { Button, Col, Collapse, Descriptions, Image, Modal, notification, Row, Space, Statistic } from "antd";
import { contractTypes } from "../types/types";

import { ReactComponent as ListWait } from '../../../assets/images/list_wait.svg';
import { ReactComponent as ListSuccess } from '../../../assets/images/list_success.svg';
import { ReactComponent as ListError } from '../../../assets/images/list_error.svg';
import { useLazyViewProductDetailQuery } from "../../../redux/api/ContractApi";
import React, { useEffect, useState } from "react";
import { format, fromUnixTime } from "date-fns";
import { PlayCircleTwoTone } from "@ant-design/icons";
import { exportFile } from "../ts/CommonFunctions";
import ConfirmModal from "./ConfirmModal";
import { useDeleteProductMutation, useLazyGetStatuteOfProductQuery } from "../../../redux/api/ProductApi";

interface ModalProps {
    visible: boolean;
    toggleModal: () => void;
    id: number;
    toggleEdit: () => void;
    triggerSearch: () => void;
}

const contractStatusIcon: Record<number, { icon: React.ReactNode, title: string }> = {
    [contractTypes.NOTSUBMIT]: { icon: <ListWait />, title: "Chưa tiếp nhận" },
    [contractTypes.PROCESSING]: { icon: <ListWait />, title: "Đang xử lý" },
    [contractTypes.DENY]: { icon: <ListError />, title: "Không ký hợp đồng" },
    [contractTypes.SIGNED]: { icon: <ListSuccess />, title: "Đã ký hợp đồng" },
}

const ProductDetailModal = ({ visible, toggleModal, id, toggleEdit, triggerSearch }: ModalProps) => {
    const [trigger, response] = useLazyViewProductDetailQuery();
    const [statuteListTrigger, statuteListResponse] = useLazyGetStatuteOfProductQuery();
    const [deleteVisible, setDeleteVisible] = useState(false);

    const [deleteProduct, { isLoading }] = useDeleteProductMutation();

    useEffect(() => {
        trigger(id);
        statuteListTrigger(id);
    }, [trigger, id, statuteListTrigger])

    const downloadFile = (url: string, fileName: string) => {
        exportFile(url, fileName);
    }

    const toggleDeleteModal = () => {
        setDeleteVisible(!deleteVisible);
    }

    const onDeleteProductClick = () => {
        deleteProduct(id).unwrap().then(res => {
            notification.success({
                message: "Xoá tài sản thành công!"
            });
            triggerSearch();
            toggleModal();
        }).catch(err => {
            notification.error({
                message: err.data?.message || "Xoá tài sản thất bại!"
            })
        })
    }

    const onEditProductClick = () => {
        toggleEdit();
        toggleModal();
    }

    return (
        <Modal
            style={{ minWidth: 1000 }}
            visible={visible}
            onCancel={toggleModal}
            className="custom-modal"
            footer={false}
            bodyStyle={{ padding: '24px 50px' }}
        >
            <div style={{ width: '100%', textAlign: 'center', marginTop: 20, fontSize: 30 }}>Thông tin tài sản đấu giá</div>
            <Row style={{ marginTop: 45 }} justify="center">
                <Col span={20}>
                    <Descriptions>
                        <Descriptions.Item label="Trạng thái hợp đồng" labelStyle={{ fontSize: 16 }} span={1}>
                            {response.data?.contract && !isNaN(response.data?.contract?.status as any) ?
                                <div style={{ display: 'flex', alignItems: 'center', fontSize: 16 }}>
                                    {contractStatusIcon[response.data.contract.status]?.icon || <></>}
                                    <strong style={{ marginLeft: 5, whiteSpace: 'nowrap' }}>{contractStatusIcon[response.data.contract.status]?.title || "-"}
                                    </strong>
                                </div> : <></>}
                        </Descriptions.Item>
                        <Descriptions.Item label="Thời gian đăng ký" labelStyle={{ fontSize: 16 }} span={1}>
                            <strong style={{ fontSize: 16 }}>
                                {response.data?.contract?.createdDate ? format(response.data.contract.createdDate, "HH:mm dd/MM/yyyy") : "-"}
                            </strong>
                        </Descriptions.Item>
                    </Descriptions>
                    <Descriptions>
                        <Descriptions.Item label="Tên tài sản" labelStyle={{ fontSize: 16 }} span={1}>
                            <strong style={{ fontSize: 16 }}>
                                {response.data?.productName || "-"}
                            </strong>
                        </Descriptions.Item>
                    </Descriptions>
                    <Descriptions>
                        <Descriptions.Item label="Nhóm tài sản" labelStyle={{ fontSize: 16 }} span={1}>
                            <strong style={{ fontSize: 16 }}>
                                {response.data?.categories?.[0]?.name || "-"}
                            </strong>
                        </Descriptions.Item>
                        <Descriptions.Item label="Giá khởi điểm" labelStyle={{ fontSize: 16 }} span={1}>
                            <Statistic
                                value={response.data?.startPrice || 0}
                                groupSeparator="."
                                decimalSeparator=","
                                precision={0}
                                valueStyle={{ fontSize: 16, fontFamily: 'Quicksand', fontWeight: 'bold' }}
                            />
                        </Descriptions.Item>
                    </Descriptions>
                    <Descriptions>
                        <Descriptions.Item label="Mô tả tài sản" labelStyle={{ fontSize: 16 }} span={1}>
                            <strong style={{ fontSize: 16 }}>
                                {response.data?.description || "-"}
                            </strong>
                        </Descriptions.Item>
                    </Descriptions>

                    <Collapse ghost>
                        {statuteListResponse.data?.map(item => {
                            return (
                                <Collapse.Panel
                                    className="statute-collapse"
                                    key={item.statuteId}
                                    header={<span style={{ fontSize: 16 }}>Mã quy chế: <strong>{item.code}</strong></span>}>
                                    <Descriptions>
                                        <Descriptions.Item label="Mã tài sản" labelStyle={{ fontSize: 16 }}>
                                            <strong style={{ fontSize: 16 }}>{item.productCode}</strong>
                                        </Descriptions.Item>
                                    </Descriptions>
                                    <Descriptions>
                                        <Descriptions.Item label="Thời gian đấu giá tài sản" labelStyle={{ fontSize: 16 }}>
                                            <strong style={{ fontSize: 16 }}>{format(fromUnixTime(item.startDate / 1000), "HH:mm:ss dd/MM/yyyy")}</strong>
                                        </Descriptions.Item>
                                    </Descriptions>
                                    <Descriptions>
                                        <Descriptions.Item label="File biên bản" labelStyle={{ fontSize: 16 }}>
                                            {item.fileBienBan.length ?
                                                <div
                                                    className="anchor-text"
                                                    style={{ color: '#40a9ff' }}
                                                    onClick={() => downloadFile(item.fileBienBan[0].fileUrl, item.fileBienBan[0].fileName)}>
                                                    {item.fileBienBan[0].fileName}</div>
                                                : <strong>Không có file biên bản</strong>
                                            }
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Collapse.Panel>
                            )
                        })}
                    </Collapse>

                    <div className="font-size-16 font-bold" style={{ marginTop: 30 }}>Hình ảnh tài sản:</div>
                    <p style={{ fontStyle: "italic" }}>(Vui lòng chọn hình ảnh có định dạng JPG, JPEG, PNG có dung lượng nhỏ hơn 5 MB)</p>
                    <Row gutter={[15, 15]} style={{ marginTop: 30 }}>
                        {response.data?.fileHoSos?.filter(item => item.storageType === 'image' || item.storageType === "avatar")?.map((item, index) => (
                            <Col key={index}>
                                <Image width={150} src={item.fileUrl} />
                                <div style={{ textAlign: 'center' }} className="text-ellipsis">{item.fileName}</div>
                            </Col>
                        ))}
                    </Row>

                    {response.data?.fileHoSos?.filter(item => item.storageType === 'video').length ?
                        <>
                            <div className="font-size-16 font-bold" style={{ marginTop: 45 }}>Video tài sản:</div>
                            <p style={{ fontStyle: "italic" }}>(Vui lòng chọn video có định dạng WMP, MP4 có dung lượng nhỏ hơn 25 MB)</p>
                            <Row gutter={[15, 15]} style={{ marginTop: 30 }}>
                                {response.data?.fileHoSos?.filter(item => item.storageType === 'video')?.map((item, index) => (
                                    <Col key={index}>
                                        <div key={index} style={{ display: 'inline-block' }} className="video-preview" onClick={() => downloadFile(item.fileUrl, item.fileName)}>
                                            <PlayCircleTwoTone style={{ width: 150, height: 100, fontSize: 80, paddingTop: 20 }} />
                                            <div
                                                style={{
                                                    textAlign: 'center',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    marginTop: 20
                                                }}>
                                                {item.fileName}
                                            </div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </> : <></>}

                    {response.data?.fileHoSos?.filter(item => item.storageType === 'file').length ?
                        <>
                            <div className="font-size-16 font-bold" style={{ marginTop: 45, textAlign: 'center' }}>File hồ sơ:</div>
                            <Row gutter={[15, 15]} style={{ marginTop: 15 }} justify="center">
                                {response.data?.fileHoSos?.filter(item => item.storageType === 'file')?.map((item, index) => (
                                    <Col key={index}>
                                        <div key={index} className="file-preview anchor-text filename" onClick={() => downloadFile(item.fileUrl, item.fileName)}>
                                            <div className="text-ellipsis">{item.fileName}</div>
                                        </div>
                                    </Col>
                                ))}
                            </Row> </> : <></>}

                    <Row style={{ marginTop: 35, marginBottom: 35 }} justify="center">
                        <Space>
                            <Button className="button-gray" onClick={toggleModal} style={{ width: 160, height: 50 }}>ĐÓNG</Button>
                            {response.data?.contract?.status === contractTypes.NOTSUBMIT ?
                                <Button className="button-red" onClick={toggleDeleteModal} style={{ width: 160, height: 50 }}>XOÁ</Button>
                                : <></>}
                            {response.data?.contract.status === contractTypes.NOTSUBMIT || response.data?.contract.status === contractTypes.PROCESSING ?
                                <Button className="button-light-yellow" onClick={onEditProductClick} style={{ width: 160, height: 50 }}>CHỈNH SỬA</Button>
                                : <></>}
                        </Space>
                    </Row>
                </Col>
            </Row>

            {deleteVisible ?
                <ConfirmModal
                    title="Bạn có chắc chắn muốn xoá tài sản này hay không?"
                    visible={deleteVisible}
                    onCancel={toggleDeleteModal}
                    onOk={onDeleteProductClick}
                    status="error"
                    cancelText="HUỶ BỎ"
                    okText="ĐỒNG Ý"
                    isLoading={isLoading}
                /> : <></>}
        </Modal>
    )
}

export default ProductDetailModal;